/**
 * Global call state: invitations, ringing UI and the active media session.
 *
 * Whether this tab rings is not remembered here but read off the call every
 * time it changes (calls-v1 §5.1, `hooks/callRinging.ts`): your own row and
 * its `ring_until`. That is what lets an answer on the phone silence the
 * browser, and the clock here silence the browser when nothing arrives at all.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { subscribeCallEvents } from '@/api/callEvents'
import { declineCall, fetchActiveCall, fetchCall, newCallSessionId, startCall } from '@/api/calls'
import { useRooms } from '@/api/queries'
import { holdRoomTopic } from '@/api/store'
import type { Call, UUID } from '@/api/types'
import { CallPill } from '@/components/CallPill'
import {
  CallAudio,
  CallCorner,
  CallWindow,
  IncomingCallPrompt,
  QuietCallBanner,
  type CallStage,
} from '@/components/CallWindow'
import { Button, Modal } from '@/components/ui'
import { roomLabel, useRoomLabels } from '@/components/Sidebar'
import { joinedDevice, nextInvitation, ownParticipant, ringDeadline, ringState, type Invitation } from '@/hooks/callRinging'
import { useMeetingSharePrompt } from '@/hooks/useMeetingSharePrompt'
import { useMeetingWindow } from '@/hooks/useMeetingWindow'
import { useCallResync } from '@/hooks/useCallResync'
import { useCallSession, type CallPreferences } from '@/hooks/useCallSession'
import {
  MEETING_ENDED_NOTICE,
  MEETING_FULL,
  MEETING_FULL_BODY,
  endedSummary,
  joinedElsewhereNotice,
  ongoingNotice,
  replacedNotice,
} from '@/lib/callCopy'
import { focusDesktopWindow, supportsMeetingWindows } from '@/lib/desktop'
import { startRingtone, type Ringtone } from '@/lib/ringtone'

/**
 * What the call dialog is waiting to do. Nothing is asked of the server until
 * the person has looked at their own camera and pressed Join.
 */
type Pending =
  | { kind: 'start'; roomId: UUID; label: string }
  | {
      kind: 'join'
      call: Call
      label: string
      /** Opened by answering an invitation, so closing it is saying no. */
      answered: boolean
    }

interface CallContextValue {
  callsEnabled: boolean
  incoming: Call | null
  activeCall: Call | null
  /** Opens the dialog on its pre-join step. */
  openRoomCall: (roomId: UUID, label: string) => void
  /** Opens the dialog for a call already under way in the room. */
  joinExistingCall: (call: Call, label: string) => void
  acceptIncoming: () => void
  acceptIncomingAudioOnly: () => void
  declineIncoming: () => Promise<void>
}

const CallContext = createContext<CallContextValue | null>(null)

export function useCalls(): CallContextValue {
  const value = useContext(CallContext)
  if (value === null) throw new Error('useCalls must be used within CallProvider')
  return value
}

function initiatorName(call: Call): string {
  const initiator = call.initiator
  if (initiator === null) return 'Someone'
  const display = initiator.display_name.trim()
  return display === '' ? initiator.username : display
}

/**
 * A system notification for a call that arrives while the tab is not the
 * thing on screen. The browser ringtone does not carry out of a background
 * tab on every platform; this does. Only when permission is already there —
 * a call is the wrong moment to ask for it.
 */
function notifyIncoming(call: Call, onOpen: () => void): (() => void) | null {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null
  if (typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus()) {
    return null
  }
  const notification = new Notification(`${initiatorName(call)} is calling`, {
    body: call.media === 'video' ? 'Video call. Open relaii to answer.' : 'Voice call. Open relaii to answer.',
    tag: `relaii-call-${call.id}`,
    requireInteraction: true,
    silent: true,
  })
  // Clicking is answering: the window comes forward — the desktop app's too —
  // and the call opens, rather than leaving the person to find it.
  notification.onclick = () => {
    focusDesktopWindow()
    window.focus()
    onOpen()
    notification.close()
  }
  return () => notification.close()
}

export function CallProvider({ children }: { children: ReactNode }): ReactElement {
  const { user, server } = useAuth()
  const selfId = user?.id
  const callsEnabled = server?.capabilities?.calls === true
  const screenSharingEnabled = callsEnabled && server?.capabilities?.call_screen_sharing === true

  const location = useLocation()
  const navigate = useNavigate()
  const { rooms } = useRooms()
  const labels = useRoomLabels()

  const roomName = useCallback(
    (roomId: UUID | undefined): string => {
      const room = rooms.find((entry) => entry.id === roomId)
      return room === undefined ? 'this room' : roomLabel(room, labels)
    },
    [labels, rooms],
  )

  const [invitation, setInvitation] = useState<Invitation | null>(null)
  // The clock the ring is read against. Moved on when a deadline passes, so
  // the render that follows reads the ring as over.
  const [now, setNow] = useState(() => Date.now())
  const [dismissedCallId, setDismissedCallId] = useState<UUID | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [joinedCall, setJoinedCall] = useState<Call | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const [joining, setJoining] = useState(false)
  const [preferences, setPreferences] = useState<CallPreferences>({ mic: true, camera: true })
  const [startError, setStartError] = useState<string | null>(null)
  // The window is out of the way, the call is not. Only Leave call ends one.
  const [minimised, setMinimised] = useState(false)
  const meetingWindow = useMeetingWindow(joinedCall?.id ?? null, roomName(joinedCall?.room_id))
  const hadMeetingWindow = useRef(false)
  useEffect(() => {
    if (hadMeetingWindow.current && meetingWindow.target === null) setMinimised(false)
    hadMeetingWindow.current = meetingWindow.target !== null
  }, [meetingWindow.target])
  // "Share a screen as I join", waiting for the press the browser insists on.
  const [sharePromptFor, setSharePromptFor] = useState<UUID | null>(null)
  // The browser would not play the meeting without a press.
  const [unblockAudio, setUnblockAudio] = useState<(() => void) | null>(null)
  const [fullRoomId, setFullRoomId] = useState<UUID | null>(null)
  const [ended, setEnded] = useState<{ roomId: UUID; label: string; summary: string } | null>(null)

  const ringtoneRef = useRef<Ringtone | null>(null)
  const notificationRef = useRef<(() => void) | null>(null)
  // Read by callbacks that outlive the render that made them: the event
  // subscription, a notification's click and the ring effect.
  const invitationRef = useRef(invitation)
  invitationRef.current = invitation
  const joinedRef = useRef(joinedCall)
  joinedRef.current = joinedCall
  // Declined here and not yet confirmed by the server. An update that crosses
  // the decline on the wire must not bring the ring back for a moment.
  const declinedRef = useRef(new Set<UUID>())
  const pendingRef = useRef(pending)
  pendingRef.current = pending

  const stopRinging = useCallback(() => {
    ringtoneRef.current?.stop()
    ringtoneRef.current = null
    notificationRef.current?.()
    notificationRef.current = null
  }, [])

  // Never read against a clock older than the invitation itself: one adopted
  // after its ring window would otherwise ring for a commit before the
  // deadline effect moves the clock on.
  const ring =
    invitation === null
      ? 'none'
      : ringState(invitation.call, selfId, invitation.receivedAt, Math.max(now, invitation.receivedAt))
  const deadline = invitation === null ? null : ringDeadline(invitation.call, selfId, invitation.receivedAt)

  // The server says when ringing stops, and so does the clock here: a push or
  // a socket message that never arrives must not leave a browser ringing.
  useEffect(() => {
    if (deadline === null) return
    const wait = deadline - Date.now()
    if (wait <= 0) {
      setNow(Date.now())
      return
    }
    const timer = window.setTimeout(() => setNow(Date.now()), wait)
    return () => window.clearTimeout(timer)
  }, [deadline])

  // Where every newer copy of a call lands.
  const applyCall = useCallback(
    (updated: Call) => {
      if (updated.status === 'ended') {
        setInvitation((current) => (current?.call.id === updated.id ? null : current))
        setJoinedCall((current) => (current?.id === updated.id ? null : current))
        setPending((current) => (current?.kind === 'join' && current.call.id === updated.id ? null : current))
        return
      }

      // The participant list is what tells the session who has left; keep
      // the call we are in, and the one being looked at, current with it.
      setJoinedCall((current) => (current?.id === updated.id ? updated : current))
      setPending((current) =>
        current?.kind === 'join' && current.call.id === updated.id ? { ...current, call: updated } : current,
      )
      setInvitation((current) => {
        if (declinedRef.current.has(updated.id)) return current?.call.id === updated.id ? null : current
        return nextInvitation(current, updated, selfId, Date.now(), joinedRef.current?.id ?? null)
      })
    },
    [selfId],
  )

  // How many events each call has had. A read-back that went out before the
  // latest one carries an older copy, and must not undo it.
  const eventCountsRef = useRef(new Map<UUID, number>())

  useEffect(() => {
    if (!callsEnabled) return
    return subscribeCallEvents((event) => {
      const counts = eventCountsRef.current
      const id = event.payload.call.id
      counts.set(id, (counts.get(id) ?? 0) + 1)
      applyCall(event.payload.call)
    })
  }, [applyCall, callsEnabled])

  // Every call this tab is holding — the one it is in, the one ringing and
  // the one being looked at — read back from the server. A call the server no
  // longer has is over (calls-v1 §7.10, §12).
  const resyncCalls = useCallback(() => {
    const waiting = pendingRef.current
    const held = new Map<UUID, Call>()
    for (const call of [
      joinedRef.current,
      invitationRef.current?.call ?? null,
      waiting !== null && waiting.kind === 'join' ? waiting.call : null,
    ]) {
      if (call !== null) held.set(call.id, call)
    }
    const counts = eventCountsRef.current
    for (const call of held.values()) {
      const seen = counts.get(call.id) ?? 0
      void fetchCall(call.id, call.room_id)
        .then((fresh) => {
          // An update landed while this was out; it is newer than the answer.
          if ((counts.get(call.id) ?? 0) !== seen) return
          applyCall(fresh ?? { ...call, status: 'ended' })
        })
        .catch(() => undefined)
    }
  }, [applyCall])

  useCallResync(resyncCalls)

  const openRoomCall = useCallback((roomId: UUID, label: string) => {
    setStartError(null)
    setPreferences((current) => ({ ...current, mic: true, camera: true }))
    setPending({ kind: 'start', roomId, label })
  }, [])

  const joinExistingCall = useCallback((call: Call, label: string) => {
    setStartError(null)
    setPreferences((current) => ({ ...current, mic: true, camera: call.media === 'video' }))
    setPending({ kind: 'join', call, label, answered: false })
  }, [])

  // Answering in an open tab opens the pre-join; the join itself waits for
  // Join. The invitation is left to the rules: it goes when the row changes.
  const answer = useCallback((camera: boolean) => {
    const call = invitationRef.current?.call
    if (call === undefined) return
    setStartError(null)
    setPreferences((current) => ({ ...current, mic: true, camera: camera && call.media === 'video' }))
    setPending({
      kind: 'join',
      call,
      label: `Join the ${call.media === 'video' ? 'video meeting' : 'call'}`,
      answered: true,
    })
  }, [])

  const acceptIncoming = useCallback(() => answer(true), [answer])

  // Answering without a camera is a decision people make at the door, not a
  // setting they go looking for once everyone can see them.
  const acceptIncomingAudioOnly = useCallback(() => answer(false), [answer])

  // A notification's click, whenever it comes. A call that ended in the
  // meantime says so rather than opening nothing.
  const openInvitationRef = useRef<() => void>(() => undefined)
  openInvitationRef.current = () => {
    const call = invitationRef.current?.call
    if (call === undefined) {
      setNotice(MEETING_ENDED_NOTICE)
      return
    }
    answer(call.media === 'video')
  }

  // Ring while the rules say this device rings, and not a moment longer.
  // Keyed on the call's id, not the object: every call.updated is a new
  // object, and a ringtone restarted on each one is a stutter, not a ring.
  const ringingId =
    ring === 'ringing' && joinedCall === null && pending === null ? (invitation?.call.id ?? null) : null

  useEffect(() => {
    const call = invitationRef.current?.call
    if (ringingId === null || call === undefined) return
    ringtoneRef.current = startRingtone()
    notificationRef.current = notifyIncoming(call, () => openInvitationRef.current())
    return stopRinging
  }, [ringingId, stopRinging])

  const session = useCallSession(joinedCall, user?.id, joinedCall !== null, preferences, {
    sessionId,
    onRejoin: resyncCalls,
  })
  const meetingShare = useMeetingSharePrompt(joinedCall?.id ?? null, session.startShare, roomName(joinedCall?.room_id))

  // The session stopped without anyone here hanging up: another device took
  // the meeting, or it ended under us. This tab only lets go and, for a
  // handover, says where the meeting went (calls-v1 §7.3).
  const sessionEnd = session.sessionEnd
  useEffect(() => {
    if (sessionEnd === null) return
    const over = joinedRef.current
    setJoinedCall(null)
    if (sessionEnd.reason === 'replaced') {
      setNotice(replacedNotice(sessionEnd.deviceKind))
    } else if (over !== null) {
      // Ended under us, not left: say what it was, and offer it again.
      const names = over.participants
        .filter((participant) => participant.user != null && participant.user.id !== selfId)
        .map((participant) => participant.user?.display_name.trim() || participant.user?.username || 'Someone')
      setEnded({
        roomId: over.room_id,
        label: roomName(over.room_id),
        summary: endedSummary(over.media, over.started_at, Date.now(), names),
      })
    }
    // The end is the signal; the names are read as they were.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionEnd])

  // No seat left. The meeting is let go and the dialog says so in its own
  // words rather than leaving an error on an empty stage.
  const sessionError = session.error
  useEffect(() => {
    if (sessionError !== MEETING_FULL) return
    const refused = joinedRef.current
    if (refused === null) return
    setFullRoomId(refused.room_id)
    setJoinedCall(null)
  }, [sessionError])

  // The room's topic carries the call's own updates. It stays joined for as
  // long as this tab is in the meeting, wherever the person is reading.
  const joinedRoomId = joinedCall?.room_id
  useEffect(() => (joinedRoomId === undefined ? undefined : holdRoomTopic(joinedRoomId)), [joinedRoomId])

  // The only place a call is created or entered. Everything before this is a
  // dialog the person can still walk away from.
  const confirmJoin = useCallback(async () => {
    if (pending === null) return
    setJoining(true)
    setStartError(null)
    try {
      // Entering a second call means leaving the first: the server would
      // otherwise keep us as joined in a room we are no longer in.
      // A leave that does not reach the server still ends the meeting here:
      // its media and channel are already gone, and a call kept on screen
      // with nothing under it is a meeting nobody can hear or leave. The
      // server lets the seat go once the channel it was held by is gone.
      if (joinedCall !== null && (pending.kind === 'start' || pending.call.id !== joinedCall.id)) {
        try {
          await session.hangUp()
        } catch {
          // As above; the join goes ahead.
        } finally {
          setJoinedCall(null)
        }
      }
      // A fresh session for every attempt: the server tells a reconnect from
      // another device taking over by whether this is the id it holds.
      const attempt = newCallSessionId()
      if (pending.kind === 'start') {
        // Always a video meeting. Starting with your own camera off is a
        // choice about you; as `audio` it took the camera away from everyone
        // who joined after.
        const entry = await startCall(pending.roomId, 'video', attempt)
        setSessionId(entry.sessionId)
        setJoinedCall(entry.call)
        setSharePromptFor(preferences.shareOnJoin === true ? entry.call.id : null)
      } else {
        // Joining is the answer; there is no invitation left to show for it.
        setInvitation((current) => (current?.call.id === pending.call.id ? null : current))
        setSessionId(attempt)
        setJoinedCall(pending.call)
        setSharePromptFor(preferences.shareOnJoin === true ? pending.call.id : null)
      }
      // Ticked for this meeting, not for every one after it.
      setPreferences((current) => ({ ...current, shareOnJoin: false }))
      setPending(null)
      // A meeting just joined opens in full. Leaving a minimised one for it
      // sets the call twice in one batch, so the reset on "no call" never runs.
      setMinimised(false)
    } catch (caught: unknown) {
      setStartError(caught instanceof Error ? caught.message : 'The call could not be started.')
    } finally {
      setJoining(false)
    }
  }, [joinedCall, pending, preferences.shareOnJoin, session])

  const declineIncoming = useCallback(async () => {
    const call = invitationRef.current?.call
    if (call === undefined) return
    declinedRef.current.add(call.id)
    setInvitation(null)
    await declineCall(call.id)
  }, [])

  // Closing the pre-join of an invitation is saying no to it — while it is
  // still one. Someone who has meanwhile joined from their phone has not
  // declined anything (calls-v1 §5.1 rule 4). The row is read from the copy
  // `applyCall` keeps current, not from when the dialog opened.
  const cancelPending = useCallback(() => {
    const closing = pending
    setPending(null)
    setStartError(null)
    if (closing === null || closing.kind !== 'join' || !closing.answered) return
    if (ownParticipant(closing.call, selfId)?.status !== 'invited') return
    declinedRef.current.add(closing.call.id)
    setInvitation((current) => (current?.call.id === closing.call.id ? null : current))
    void declineCall(closing.call.id).catch(() => undefined)
  }, [pending, selfId])

  // Reading another room is not leaving the meeting; the window gets out of
  // the way and the pill keeps it.
  useEffect(() => {
    if (joinedCall !== null) setMinimised(true)
    // The path is the signal, not the call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (joinedCall !== null) return
    setMinimised(false)
    setSessionId(null)
  }, [joinedCall])

  // "Share a screen as I join" is offered on the stage, one press away. Asked
  // for from here, seconds after the click that joined, the browser has
  // forgotten there ever was a click and refuses the picker.
  useEffect(() => {
    if (joinedCall === null) {
      setSharePromptFor(null)
      setUnblockAudio(null)
    }
  }, [joinedCall])

  // Everything the others send that can be heard: their voices, and the sound
  // of a shared window.
  const audible = useMemo((): MediaStream[] => {
    const streams = session.remoteTracks.map((track) => track.stream)
    const shared = session.share.remote
    return shared === null || streams.includes(shared) ? streams : [...streams, shared]
  }, [session.remoteTracks, session.share.remote])

  const openRoom = useCallback((roomId: UUID) => navigate(`/rooms/${roomId}`), [navigate])

  const incoming = invitation?.call ?? null

  const value = useMemo(
    (): CallContextValue => ({
      callsEnabled,
      incoming,
      activeCall: joinedCall,
      openRoomCall,
      joinExistingCall,
      acceptIncoming,
      acceptIncomingAudioOnly,
      declineIncoming,
    }),
    [
      acceptIncoming,
      acceptIncomingAudioOnly,
      callsEnabled,
      declineIncoming,
      incoming,
      joinExistingCall,
      joinedCall,
      openRoomCall,
    ],
  )

  // Leave call, from the pill or the stage. The meeting is let go here even
  // when the leave request fails: the media is already down, and the server's
  // clean-up gives the seat back to a device that never returns.
  const leaveMeeting = (): void => {
    void session
      .hangUp()
      .catch(() => undefined)
      .finally(() => setJoinedCall(null))
  }

  // What the person is in if they press Join here, when that is somewhere else.
  const elsewhereFor = (call: Call): string | null => {
    if (call.id === joinedCall?.id) return null
    const seat = joinedDevice(call, selfId)
    return seat === null ? null : joinedElsewhereNotice(seat.deviceKind)
  }

  // A pre-join comes first even during a meeting. Looking at another call is
  // not leaving this one: it carries on under the pill until Join is actually
  // pressed there (calls-v1 §7.11).
  const stage: CallStage | null =
    pending !== null
      ? {
          kind: 'prejoin',
          title: pending.label,
          media: pending.kind === 'join' ? pending.call.media : 'video',
          call: pending.kind === 'join' ? pending.call : null,
          context:
            pending.kind === 'join'
              ? `${roomName(pending.call.room_id)} · started by ${initiatorName(pending.call)}`
              : roomName(pending.roomId),
          elsewhere: pending.kind === 'join' ? elsewhereFor(pending.call) : null,
        }
      : joinedCall !== null && !minimised && meetingWindow.target === null
        ? { kind: 'in-call', title: roomName(joinedCall.room_id), call: joinedCall }
        : null

  const sharingName =
    joinedCall === null || joinedCall.sharing_user_id == null || joinedCall.sharing_user_id === user?.id
      ? null
      : (joinedCall.participants.find((participant) => participant.user?.id === joinedCall.sharing_user_id)?.user
          ?.display_name ?? 'Someone')

  const invitedRoom = invitation === null ? '' : roomName(invitation.call.room_id)

  return (
    <CallContext.Provider value={value}>
      {children}
      {meetingShare.prompt}
      {callsEnabled ? (
        <>
          {joinedCall === null ? null : (
            <CallAudio streams={audible} onBlocked={(retry) => setUnblockAudio(() => retry)} />
          )}
          <CallCorner
            pill={
              joinedCall !== null && (minimised || pending !== null || meetingWindow.target !== null) ? (
                <CallPill
                  where={roomName(joinedCall.room_id)}
                  startedAt={joinedCall.started_at}
                  micEnabled={session.micEnabled}
                  sharedBy={sharingName}
                  onToggleMic={() => session.setMicEnabled(!session.micEnabled)}
                  onOpen={() => {
                    setMinimised(false)
                    if (meetingWindow.target !== null) meetingWindow.open()
                  }}
                  onLeave={leaveMeeting}
                />
              ) : null
            }
          >
            {invitation !== null && ring === 'ringing' && pending === null ? (
              <IncomingCallPrompt
                call={invitation.call}
                where={invitedRoom}
                busyIn={joinedCall === null ? null : roomName(joinedCall.room_id)}
                selfUserId={selfId}
                deadline={deadline}
                onAccept={acceptIncoming}
                onAcceptAudioOnly={acceptIncomingAudioOnly}
                onDecline={() => void declineIncoming()}
                onReply={() => {
                  const roomId = invitation.call.room_id
                  void declineIncoming()
                  openRoom(roomId)
                }}
              />
            ) : null}
            {unblockAudio === null ? null : (
              <QuietCallBanner
                text="Your browser is holding the meeting’s sound back."
                action={{
                  label: 'Tap to hear',
                  onClick: () => {
                    unblockAudio()
                    setUnblockAudio(null)
                  },
                }}
                onDismiss={() => setUnblockAudio(null)}
              />
            )}
            {/* After the ring window: no sound, and a way in for as long as the
                meeting runs (calls-v1 §5.1 rule 3). */}
            {invitation !== null && ring === 'ongoing' && pending === null && dismissedCallId !== invitation.call.id ? (
              <QuietCallBanner
                text={ongoingNotice(invitedRoom)}
                action={{ label: 'Join', onClick: () => answer(invitation.call.media === 'video') }}
                onDismiss={() => setDismissedCallId(invitation.call.id)}
              />
            ) : null}
            {notice === null ? null : <QuietCallBanner text={notice} onDismiss={() => setNotice(null)} />}
            {meetingWindow.error === null ? null : <QuietCallBanner text={meetingWindow.error} onDismiss={meetingWindow.dismissError} />}
          </CallCorner>
          <CallWindow
            stage={stage}
            selfUserId={user?.id}
            session={{ ...session, error: session.error ?? startError }}
            preferences={preferences}
            onPreferencesChange={setPreferences}
            joining={joining}
            screenSharingEnabled={screenSharingEnabled}
            onJoin={() => void confirmJoin()}
            onCancel={cancelPending}
            onMinimise={() => setMinimised(true)}
            onPopOut={supportsMeetingWindows() ? meetingWindow.open : undefined}
            onHangUp={leaveMeeting}
            onChat={
              joinedCall === null
                ? undefined
                : () => {
                    // The room's own timeline is the meeting's chat: a
                    // meeting should leave something behind.
                    setMinimised(true)
                    openRoom(joinedCall.room_id)
                  }
            }
            sharePrompt={joinedCall !== null && sharePromptFor === joinedCall.id}
            onSharePromptDone={() => setSharePromptFor(null)}
          />
          {joinedCall === null || meetingWindow.target === null ? null : (
            <CallWindow
              stage={{ kind: 'in-call', title: roomName(joinedCall.room_id), call: joinedCall }}
              selfUserId={user?.id}
              session={{ ...session, startShare: meetingShare.requestShare }}
              preferences={preferences}
              onPreferencesChange={setPreferences}
              joining={false}
              screenSharingEnabled={screenSharingEnabled}
              onJoin={() => {}}
              onCancel={() => {}}
              onHangUp={leaveMeeting}
              portalTarget={meetingWindow.target}
              onDock={() => { meetingWindow.close(); setMinimised(false) }}
              onChat={() => openRoom(joinedCall.room_id)}
              sharePrompt={sharePromptFor === joinedCall.id}
              onSharePromptDone={() => setSharePromptFor(null)}
            />
          )}
          <Modal
            open={fullRoomId !== null}
            onClose={() => setFullRoomId(null)}
            title={MEETING_FULL}
            size="sm"
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (fullRoomId !== null) openRoom(fullRoomId)
                    setFullRoomId(null)
                  }}
                >
                  Write in the channel
                </Button>
                <Button variant="primary" onClick={() => setFullRoomId(null)}>
                  Wait for a seat
                </Button>
              </>
            }
          >
            <p>{MEETING_FULL_BODY}</p>
          </Modal>
          <Modal
            open={ended !== null}
            onClose={() => setEnded(null)}
            title="The meeting ended"
            size="sm"
            footer={
              <>
                <Button variant="ghost" onClick={() => setEnded(null)}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (ended !== null) openRoomCall(ended.roomId, 'Video meeting')
                    setEnded(null)
                  }}
                >
                  Start again
                </Button>
              </>
            }
          >
            <p>{ended?.summary}</p>
          </Modal>
        </>
      ) : null}
    </CallContext.Provider>
  )
}

/** Load any active call when opening a room. */
export function useRoomCall(roomId: UUID | undefined): Call | null {
  const { callsEnabled, activeCall } = useCalls()
  const [call, setCall] = useState<Call | null>(null)

  // Asked again whenever the tab or the connection comes back: the room's
  // bar should not show a meeting that ended while nobody was looking.
  const [resyncs, setResyncs] = useState(0)
  useCallResync(useCallback(() => setResyncs((count) => count + 1), []))

  useEffect(() => {
    if (!callsEnabled || roomId === undefined) {
      setCall(null)
      return
    }

    let cancelled = false
    void fetchActiveCall(roomId)
      .then((active) => {
        if (!cancelled) setCall(active)
      })
      // The bar keeps what it last knew; the next resync or call event asks again.
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [callsEnabled, resyncs, roomId])

  // A call ending, or the one we are in, reaches this room's button too.
  useEffect(() => {
    return subscribeCallEvents((event) => {
      const updated = event.payload.call
      if (updated.room_id !== roomId) return
      setCall(updated.status === 'ended' ? null : updated)
    })
  }, [roomId])

  return activeCall?.room_id === roomId ? activeCall : call
}

